import React, { useEffect, useState } from 'react'
import './index.css';
import { Alert, Col, Row, Button, Modal, Spinner, Card, Badge } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { useLocation } from 'react-router-dom';
import { CartList } from '../..';
import DynamicBreakDownAccordion from '../../../../../components/DynamicBreakDownAccordion';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { getLocalDateString } from '../../../../../utility/Utility';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faWallet,
  faCoins,
  faCreditCard,
  faMoneyBillWave,
  faCalendarAlt,
  faChartLine,
  faCheckCircle,
  faExclamationTriangle,
  faShoppingCart,
} from '@fortawesome/free-solid-svg-icons';
import { useSettleCartPaymentMutation } from '../../../../../redux/services/restaurantServices';
import { useCartFullData } from '../../../../../hooks/useCartFullData';

const Sales = () => {
  const restaurantId = useSelector(
    (state: any) => state.restaurant.selectedRestaurantDetails.id
  );

  const { setValue } = useForm({
    defaultValues: {
      transactionDate: new Date().toISOString().split('T')[0]
    }
  });

  const location = useLocation();
  const { carts } = location.state as { carts: CartList };
  const cartId: string = carts.id;
  const [selectedDate, setSelectedDate] = useState<string>(getLocalDateString());
  const [showSettleModal, setShowSettleModal] = useState(false);
  
  const [settleCartPayment, { isLoading: isSettling }] = useSettleCartPaymentMutation();
  
  const {
    // Cart Person
    cartPerson,
    
    // Sales Wallet
    salesDailySummary,
    
    // Purchase Wallet
    purchaseWallet: purchaseWalletData,
    
    // Cart Statistics
    cartStatistics,
    
    // Combined transactions for accordion
    groupedTransactions,
    
    // Loading states
    loading,
    loadingPurchaseWallet,
    
    // Error states
    error,
    errorCartPerson,
    errorPurchaseWallet,
    
  } = useCartFullData({
    cartId,
    selectedDate
  });

  useEffect(() => {
    setValue("transactionDate", selectedDate);
  }, [setValue, selectedDate]);

  const handleSettleClick = () => {
    setShowSettleModal(true);
  };

  const handleCloseModal = () => {
    setShowSettleModal(false);
  };

  const handleConfirmSettle = async () => {
    try {
      const amount = purchaseWalletData?.payableToPlatform || 0;
      
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const response = await settleCartPayment({
        cartId,
        amount,
        restaurantId
      }).unwrap();
      
      toast.success('Payment settled successfully!');
      setShowSettleModal(false);
            
    } catch (error) {
      console.error('❌ Settlement failed:', error);
      toast.error('Failed to settle payment. Please try again.');
    }
  };

  // Loading Components
  const SalesSummaryLoading = () => (
    <Row className="g-4">
      {[...Array(3)].map((_, index) => (
        <Col lg={4} md={6} key={index}>
          <Card className="sales-card h-100">
            <Card.Body className="text-center">
              <div className="placeholder-glow">
                <div className="placeholder col-3 rounded-circle mb-3" style={{ height: '60px' }}></div>
                <span className="placeholder col-8 mb-2"></span>
                <span className="placeholder col-6"></span>
              </div>
            </Card.Body>
          </Card>
        </Col>
      ))}
    </Row>
  );

  const TransactionSummaryLoading = () => (
    <Row className="g-4 mt-3">
      {[...Array(2)].map((_, index) => (
        <Col lg={6} key={index}>
          <Card className="transaction-card h-100">
            <Card.Body className="text-center">
              <div className="placeholder-glow">
                <div className="placeholder col-3 rounded-circle mb-3" style={{ height: '50px' }}></div>
                <span className="placeholder col-7 mb-2"></span>
                <span className="placeholder col-5"></span>
              </div>
            </Card.Body>
          </Card>
        </Col>
      ))}
    </Row>
  );

  const isLoading = loading;
  const hasError = error;
  
  // Check if we have any transaction groups with data
  const hasTransactions = Object.keys(groupedTransactions).length > 0 && 
                         Object.values(groupedTransactions).some((group: any) => 
                           group.transactions && group.transactions.length > 0
                         );

  // Get sales data from cart statistics (primary source) with fallback to sales wallet
  const cashSales = cartStatistics?.cashSales ?? salesDailySummary?.cashSales ?? 0;
  const cardSales = cartStatistics?.cardSales ?? salesDailySummary?.cardSales ?? 0;

  return (
    <div className="py-3">
      {/* Sales Summary Cards - Financial Overview using Purchase Wallet */}
      <div className="sales-summary-section">
        <h4 className="section-title">
          <FontAwesomeIcon icon={faWallet} className="me-2" />
          Financial Overview
        </h4>

        {loadingPurchaseWallet ? (
          <SalesSummaryLoading />
        ) : errorPurchaseWallet ? (
          <Alert variant="danger" className="error-alert">
            <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
            <Alert.Heading>Error Loading Wallet Data</Alert.Heading>
            Unable to load purchase wallet data. Please try again.
          </Alert>
        ) : (
          <Row className="g-4">
            {/* Balance Card */}
            <Col lg={4} md={6}>
              <Card className="sales-card balance-card h-100">
                <Card.Body className="text-center">
                  <div className="card-icon balance-icon">
                    <FontAwesomeIcon icon={faWallet} />
                  </div>
                  <h6 className="card-title">Current Balance</h6>
                  <h3 className="card-amount text-primary">
                    Rs. {(purchaseWalletData?.balance || 0).toFixed(2)}
                  </h3>
                  <small className="text-muted">Available funds</small>
                </Card.Body>
              </Card>
            </Col>

            {/* My Earnings Card */}
            <Col lg={4} md={6}>
              <Card className="sales-card earnings-card h-100">
                <Card.Body className="text-center">
                  <div className="card-icon earnings-icon">
                    <FontAwesomeIcon icon={faCoins} />
                  </div>
                  <h6 className="card-title">My Earnings</h6>
                  <h3 className="card-amount text-success">
                    Rs. {(purchaseWalletData?.myEarnings || 0).toFixed(2)}
                  </h3>
                  <small className="text-muted">Total earned</small>
                </Card.Body>
              </Card>
            </Col>

            {/* Payable to Platform Card */}
            <Col lg={4} md={6}>
              <Card className="sales-card payable-card h-100">
                <Card.Body className="text-center">
                  <div className="card-icon payable-icon">
                    <FontAwesomeIcon icon={faExclamationTriangle} />
                  </div>
                  <h6 className="card-title">Payable to Platform</h6>
                  <h3 className="card-amount text-warning">
                    Rs. {(purchaseWalletData?.payableToPlatform || 0).toFixed(2)}
                  </h3>
                  <div className="mt-3">
                    {(purchaseWalletData?.payableToPlatform || 0) > 0 ? (
                      <Button
                        variant="success"
                        className="settle-btn"
                        onClick={handleSettleClick}
                        disabled={isSettling || loadingPurchaseWallet}
                      >
                        {isSettling ? (
                          <>
                            <Spinner size="sm" className="me-2" />
                            Settling...
                          </>
                        ) : (
                          <>
                            <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
                            Settle Now
                          </>
                        )}
                      </Button>
                    ) : (
                      <Badge bg="success" className="px-3 py-2">
                        <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
                        All Settled
                      </Badge>
                    )}
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}
      </div>

      {/* Cart Info Card */}
      {cartPerson && !errorCartPerson && (
        <div className="mt-4">
          <Card className="cart-info-card">
            <Card.Body>
              <Row className="align-items-center">
                <Col md={8}>
                  <div className="d-flex align-items-center">
                    <div className="cart-icon me-3">
                      <FontAwesomeIcon icon={faShoppingCart} size="2x" className="text-white" />
                    </div>
                    <div>
                      <h6 className="mb-1 text-white">{cartPerson.displayName || 'Cart Person'}</h6>
                      <div className="d-flex flex-wrap gap-3">
                        <small className="text-white-50">
                          <strong>Email:</strong> {cartPerson.email || 'N/A'}
                        </small>
                        <small className="text-white-50">
                          <strong>Phone:</strong> {cartPerson.phoneNumber || 'N/A'}
                        </small>
                        <small className="text-white-50">
                          <strong>Status:</strong> {cartPerson.status || 'N/A'}
                        </small>
                      </div>
                    </div>
                  </div>
                </Col>
             
              </Row>
            </Card.Body>
          </Card>
        </div>
      )}

      {/* Transactions Section */}
      <div className="py-5">
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mb-4">
          <h4 className="section-title mb-0">
            <FontAwesomeIcon icon={faCreditCard} className="me-2" />
            Daily Transactions
          </h4>
          <div className="date-selector">
            <div className="input-group">
              <span className="input-group-text">
                <FontAwesomeIcon icon={faCalendarAlt} />
              </span>
              <input
                type="date"
                className="form-control"
                value={selectedDate}
                onChange={(e) => {
                  const newDate = e.target.value;
                  setSelectedDate(newDate);
                  setValue("transactionDate", newDate);
                }}
                max={getLocalDateString()}
                disabled={isLoading}
              />
            </div>
          </div>
        </div>

        {/* Transaction Summary Cards */}
        {isLoading ? (
          <TransactionSummaryLoading />
        ) : hasError ? (
          <Alert variant="danger" className="error-alert">
            <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
            <Alert.Heading>Error Loading Transactions</Alert.Heading>
            Unable to load transaction data. Please try again.
          </Alert>
        ) : (
          <Row className="g-4">
            <Col lg={6}>
              <Card className="transaction-card cash-card h-100">
                <Card.Body className="text-center">
                  <div className="card-icon cash-icon">
                    <FontAwesomeIcon icon={faMoneyBillWave} />
                  </div>
                  <h6 className="card-title">Cash Sales</h6>
                  <h3 className="card-amount text-success">
                    Rs. {cashSales.toFixed(2)}
                  </h3>
                  <small className="text-muted">
                    {cartStatistics ? 'From cart statistics' : 'From sales wallet'}
                  </small>
                </Card.Body>
              </Card>
            </Col>
            <Col lg={6}>
              <Card className="transaction-card card-sales h-100">
                <Card.Body className="text-center">
                  <div className="card-icon card-icon-style">
                    <FontAwesomeIcon icon={faCreditCard} />
                  </div>
                  <h6 className="card-title">Card Sales</h6>
                  <h3 className="card-amount text-info">
                    Rs. {cardSales.toFixed(2)}
                  </h3>
                  <small className="text-muted">
                    {cartStatistics ? 'From cart statistics' : 'From sales wallet'}
                  </small>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}

        {/* Transaction Breakdown */}
        <div className="transaction-breakdown py-5">
          <h4 className="breakdown-title">
            <FontAwesomeIcon icon={faChartLine} className="me-2" />
            Transaction Breakdown
          </h4>

          {isLoading ? (
            <Card className="breakdown-loading">
              <Card.Body>
                <div className="d-flex justify-content-center align-items-center py-5">
                  <div className="text-center">
                    <Spinner animation="border" className="mb-3" style={{ color: '#FF1001' }} />
                    <div className="text-muted">Loading transaction data...</div>
                  </div>
                </div>
              </Card.Body>
            </Card>
          ) : hasError ? (
            <Alert variant="danger" className="error-alert">
              <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
              <Alert.Heading>Error Loading Data</Alert.Heading>
              Unable to load transaction data. Please try again.
            </Alert>
          ) : (
            hasTransactions ? (
              <div className="breakdown-content">
                <DynamicBreakDownAccordion
                  transactionData={groupedTransactions}
                />
              </div>
            ) : (
              <Alert variant="info" className="no-data-alert">
                <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                <Alert.Heading>No Transaction Data</Alert.Heading>
                <p className="mb-0">
                  No transactions found for {selectedDate}. Try selecting a different date.
                </p>
              </Alert>
            )
          )}
        </div>
      </div>

      {/* Settlement Modal */}
      <Modal show={showSettleModal} onHide={handleCloseModal} centered size="lg">
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="d-flex align-items-center">
            <div className="modal-icon me-3">
              <FontAwesomeIcon icon={faCheckCircle} />
            </div>
            Confirm Payment Settlement
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-0">
          <div className="settlement-details">
            <Alert variant="warning" className="mb-4">
              <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
              This action cannot be undone. Please verify the details before proceeding.
            </Alert>

            <Row className="settlement-info">
              <Col md={6}>
                <div className="info-item">
                  <label>Cart ID</label>
                  <div className="info-value">
                    <Badge bg="secondary" className="px-3 py-2">{cartId}</Badge>
                  </div>
                </div>
              </Col>
              <Col md={6}>
                <div className="info-item">
                  <label>Settlement Amount</label>
                  <div className="info-value">
                    <h4 className="text-success mb-0">
                      Rs. {(purchaseWalletData?.payableToPlatform || 0).toFixed(2)}
                    </h4>
                  </div>
                </div>
              </Col>
            </Row>

            {cartPerson && (
              <Row className="mt-3">
                <Col md={12}>
                  <div className="info-item">
                    <label className="text-muted mb-1">Cart Person</label>
                    <div className="info-value">
                      <Badge bg="info" className="px-3 py-2 me-2">
                        {cartPerson.displayName || cartPerson.email || cartPerson.phoneNumber}
                      </Badge>
                    </div>
                  </div>
                </Col>
              </Row>
            )}

            {isSettling && (
              <div className="settling-progress mt-4">
                <div className="d-flex align-items-center justify-content-center">
                  <Spinner animation="border" size="sm" className="me-3" style={{ color: '#FF1001' }} />
                  <span className="text-muted">Processing your settlement request...</span>
                </div>
              </div>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0">
          <Button
            variant="outline-secondary"
            onClick={handleCloseModal}
            disabled={isSettling}
            className="px-4"
          >
            Cancel
          </Button>
          <Button
            variant="success"
            onClick={handleConfirmSettle}
            disabled={isSettling}
            className="px-4"
          >
            {isSettling ? (
              <>
                <Spinner size="sm" className="me-2" />
                Processing...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
                Confirm Settlement
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Sales;